import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import dagre from "dagre";
import {
  Building2,
  Combine,
  Flame,
  Globe,
  Network,
  Plug,
  RadioTower,
  Router,
  Server,
  Shuffle,
  UserRound,
  Warehouse,
  Waypoints,
} from "lucide-react";
import {
  CloudMark,
  PILL_BORDER,
  cloudPlateClass,
} from "kontrolEdge/views/OverviewComponents/actionCenter/topologyParts";
import { HEALTH_DOT } from "kontrolEdge/views/OverviewComponents/actionCenter/ServiceScopeChips";
import { ServiceHealth } from "kontrolEdge/views/OverviewComponents/actionCenter/overviewModel";
import { resolveFabricProviderLogo } from "kontrolEdge/helpers/fabricProviderLogo";
import { resolveCloudProviderLogo } from "kontrolEdge/helpers/cloudProviderLogo";
import {
  DiagramNode,
  DiagramNodeKind,
  DiagramSpec,
  DiagramStatus,
} from "./diagramSpec";

/**
 * A drawing Archi made, drawn the way the Overview draws a topology: cards
 * with a health dot, clouds as their vendor's mark on a ringed plate, links as
 * smooth curves coloured by health — the same parts (`topologyParts`), so the
 * two read as one product. dagre only places things; the drawing is ours.
 */

const CARD_W = 212;
const CARD_H = 56;
/** A cloud's plate, and the room its caption takes below it. */
const CLOUD_R = 17;
const CLOUD_BOX = { width: 150, height: 64 };
const PAD = 24;
/** Cards land one after another, then the lines between them. */
const STAGGER_MS = 70;

const KIND_ICON: Record<
  Exclude<DiagramNodeKind, "cloud">,
  React.ComponentType<{ className?: string }>
> = {
  customer: UserRound,
  site: Building2,
  datacenter: Warehouse,
  port: Plug,
  backbone: Network,
  internet: Globe,
  dci: Shuffle,
  sdwan: Waypoints,
  firewall: Flame,
  service: Server,
  peer: Router,
  reflector: RadioTower,
  transit_gateway: Combine,
};

/** Archi's statuses in the Overview's vocabulary, so the colours are the same ones. */
const HEALTH: Record<DiagramStatus, ServiceHealth> = {
  up: "up",
  degraded: "warning",
  down: "down",
  unknown: "unknown",
};

/**
 * Kontrol Edge's teal for every line that is not in trouble — as on the
 * Overview, where a cloud line is teal until something is wrong with it. Only
 * a degraded or down link changes colour.
 */
const LINE_STROKE: Record<ServiceHealth, string> = {
  up: "stroke-semantic-bg-brand-bold",
  unknown: "stroke-semantic-bg-brand-bold",
  warning: "stroke-semantic-fg-warning",
  down: "stroke-semantic-fg-danger",
};

interface Placed {
  node: DiagramNode;
  /** Centre. */
  x: number;
  y: number;
  order: number;
}

interface Routed {
  index: number;
  /** The line's course, from dagre, ends replaced by the nodes' anchors. */
  points: [number, number][];
  /** Where its label sits, in a slot dagre kept free for it; null without a label. */
  label: [number, number] | null;
}

interface Laid {
  nodes: Placed[];
  edges: Routed[];
  width: number;
  height: number;
}

const isCloud = (node: DiagramNode) => node.kind === "cloud";

/**
 * Which cloud a cloud node is, for its logo: its name when that says it
 * ("AWS"), else its detail ("Cloud-001" · "AWS · Equinix · 1 Gbps").
 */
function cloudOf(node: DiagramNode): string {
  if (resolveCloudProviderLogo(node.label) || !node.detail) return node.label;
  return resolveCloudProviderLogo(node.detail) ? node.detail : node.label;
}
// A fresh object per node: dagre writes each node's position into the object it
// was given, so a shared one would stack every cloud on the last one placed.
const boxOf = (node: DiagramNode) =>
  isCloud(node) ? { ...CLOUD_BOX } : { width: CARD_W, height: CARD_H };

/** A label's footprint at the size it is drawn (10px bold, in a pill). */
function labelBox(text: string): { width: number; height: number } {
  // A provider's logo leads the label when it names one: room for it too.
  const logo = resolveFabricProviderLogo(text) ? 18 : 0;
  return { width: Math.ceil(text.length * 6.4) + 16 + logo, height: 20 };
}

/**
 * Left to right: what serves first, what is served after, as on the Overview.
 *
 * Labels are laid out with the nodes, not added after: dagre gives every
 * labelled link a slot of the label's size between the columns and routes the
 * line through it, so no label can land on a card, a cloud or another label.
 * Two links between the same pair get a slot each (a multigraph), instead of
 * sharing one where their labels would stack.
 */
function layout(spec: DiagramSpec, withLabels: boolean): Laid {
  const g = new dagre.graphlib.Graph({ multigraph: true });
  g.setGraph({
    rankdir: "LR",
    ranksep: withLabels ? 56 : 110,
    nodesep: 22,
    edgesep: 14,
    marginx: PAD,
    marginy: PAD,
  });
  g.setDefaultEdgeLabel(() => ({}));
  spec.nodes.forEach((n) => g.setNode(n.id, boxOf(n)));
  spec.edges.forEach((e, i) =>
    g.setEdge(
      e.from,
      e.to,
      withLabels && e.label ? { ...labelBox(e.label), labelpos: "c" } : {},
      String(i),
    ),
  );
  dagre.layout(g);
  const graph = g.graph();
  const nodes = spec.nodes
    .map((node) => ({ node, x: g.node(node.id).x, y: g.node(node.id).y }))
    .sort((a, b) => a.x - b.x || a.y - b.y)
    .map((p, order) => ({ ...p, order }));
  const byId = new Map(nodes.map((p) => [p.node.id, p]));

  const edges = spec.edges.flatMap((e, index) => {
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    const routed = g.edge({ v: e.from, w: e.to, name: String(index) }) as
      | { points?: { x: number; y: number }[]; x?: number; y?: number }
      | undefined;
    if (!from || !to || !routed) return [];
    // dagre ends a line on the node's box; ours end where the card's side or
    // the cloud's ring is, so the inner points are kept and the ends replaced.
    const inner = (routed.points ?? [])
      .slice(1, -1)
      .map((pt) => [pt.x, pt.y] as [number, number]);
    const points = [anchor(from, "out"), ...inner, anchor(to, "in")];
    const label =
      withLabels && e.label && routed.x !== undefined && routed.y !== undefined
        ? ([routed.x, routed.y] as [number, number])
        : null;
    return [{ index, points, label }];
  });
  return { nodes, edges, width: graph.width ?? 0, height: graph.height ?? 0 };
}

/** Where a link leaves a node (its right side) and where it arrives (its left). */
function anchor(p: Placed, side: "out" | "in"): [number, number] {
  const half = isCloud(p.node) ? CLOUD_R + 2 : CARD_W / 2;
  return [side === "out" ? p.x + half : p.x - half, p.y];
}

/**
 * A smooth line through a link's course (a uniform B-spline, as d3's
 * `curveBasis`), starting and ending exactly on its anchors. With only the two
 * ends it is the Overview's cloud line: leaves level, bends once, arrives level.
 */
function curve(points: [number, number][]): string {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return "";
  if (points.length === 2) {
    const middle = (first[0] + last[0]) / 2;
    return `M${first[0]},${first[1]} C${middle},${first[1]} ${middle},${last[1]} ${last[0]},${last[1]}`;
  }
  const at = (i: number) => points[Math.min(i, points.length - 1)] ?? last;
  let d = `M${first[0]},${first[1]}`;
  const [a, b] = [at(0), at(1)];
  d += ` L${(5 * a[0] + b[0]) / 6},${(5 * a[1] + b[1]) / 6}`;
  for (let i = 2; i <= points.length; i++) {
    const [p0, p1, p] = [at(i - 2), at(i - 1), at(i)];
    d += ` C${(2 * p0[0] + p1[0]) / 3},${(2 * p0[1] + p1[1]) / 3} ${(p0[0] + 2 * p1[0]) / 3},${(p0[1] + 2 * p1[1]) / 3} ${(p0[0] + 4 * p1[0] + p[0]) / 6},${(p0[1] + 4 * p1[1] + p[1]) / 6}`;
  }
  return `${d} L${last[0]},${last[1]}`;
}

/**
 * What Archi proposes flashes orange neon a few times once the drawing has
 * landed, so the eye goes to what would be new before reading anything. Its
 * own colour on purpose: not the brand teal, not a health colour — "new".
 * One timing for every proposed element (`archi-new` in tailwind.config.js),
 * so they flash together.
 */
const NEON = "#FF8A00";

/**
 * The other thing a drawing can point at: what the answer is about — down,
 * degraded, without a reading, hit by an incident. Archi marks it `focus`;
 * it flashes in its health colour, keeps a ring of it, and everything else
 * steps back, so the eye lands on the problem first.
 */
const DANGER = "var(--color-semantic-fg-danger)";
const WARNING = "var(--color-semantic-fg-warning)";
const attentionColor = (status: DiagramStatus | undefined) =>
  status === "down" ? DANGER : WARNING;

/** Whether Archi pointed at anything in this drawing. */
const hasFocus = (spec: DiagramSpec) =>
  spec.nodes.some((n) => n.focus) || spec.edges.some((e) => e.focus);

/**
 * Everything a focused drawing is not pointing at: there, but quieter — and
 * back to full under the pointer, so it can still be read without leaving.
 */
const RECEDED =
  "[filter:grayscale(0.6)_opacity(0.45)] hover:[filter:none] transition-[filter]";

function Glow({
  shape,
  delayMs,
  color,
}: {
  shape: "card" | "ring";
  delayMs: number;
  color: string;
}) {
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute -inset-[3px] border-2 opacity-0 motion-safe:animate-archi-new ${
        shape === "card" ? "rounded-[10px]" : "rounded-full"
      }`}
      style={{
        borderColor: color,
        boxShadow: `0 0 18px 4px color-mix(in srgb, ${color} 67%, transparent), inset 0 0 8px color-mix(in srgb, ${color} 40%, transparent)`,
        animationDelay: `${delayMs}ms`,
      }}
    />
  );
}

/**
 * The fabric provider a link's label names (Megaport, Equinix, Console
 * Connect), as its logo on a white disc — the logos are drawn for a light
 * ground, as under a cloud's mark. Nothing when the label names none.
 */
function ProviderBadge({ text }: { text: string }) {
  const logo = resolveFabricProviderLogo(text);
  if (!logo) return <span className="w-0.5" aria-hidden />;
  return (
    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-white">
      <img
        src={logo}
        alt=""
        aria-hidden
        className="h-2.5 w-2.5 object-contain"
      />
    </span>
  );
}

const ProposedTag: React.FC = () => (
  <span className="absolute -top-2 right-2 rounded-full border border-semantic-border-brand-primary bg-semantic-bg-primary px-1.5 text-[9px] font-bold uppercase tracking-wide text-semantic-text-brand">
    Proposed
  </span>
);

/**
 * What makes a drawn element a link: a click opens it (unless the click ended
 * a drag), and `data-view-href` gives it the dashboard's "Add view" gestures —
 * right click, Cmd/Ctrl+click, middle click — like any row that opens a page.
 */
interface Opens {
  href: string | null;
  onOpen: (href: string) => void;
}

function linkProps({ href, onOpen }: Opens) {
  if (!href) return {};
  return {
    ...viewHref(href),
    role: "link",
    tabIndex: 0,
    onClick: (e: React.MouseEvent) => {
      // Cmd/Ctrl+click and middle click are the views' own gestures.
      if (e.metaKey || e.ctrlKey || e.button !== 0) return;
      onOpen(href);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onOpen(href);
    },
  };
}

function NodeCard({
  p,
  opens,
  newAt,
  receded,
}: {
  p: Placed;
  opens: Opens;
  newAt: number;
  /** The drawing points elsewhere: this one steps back. */
  receded: boolean;
}) {
  const { node } = p;
  const focus = Boolean(node.focus) && !node.planned;
  const health = HEALTH[node.status ?? "unknown"];
  const Icon =
    KIND_ICON[node.kind as Exclude<DiagramNodeKind, "cloud">] ?? Server;
  // A provider (or a data center it runs) shows its own logo, as clouds do.
  const logo = resolveFabricProviderLogo(node.label);
  return (
    <div
      {...linkProps(opens)}
      className={`absolute flex flex-col justify-center rounded-lg border bg-semantic-bg-primary px-2.5 shadow-sm transition-shadow motion-safe:animate-topology-in ${
        opens.href
          ? "cursor-pointer hover:border-semantic-border-brand-primary hover:shadow-md"
          : ""
      } ${
        focus
          ? node.status === "down"
            ? "border-semantic-fg-danger shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-semantic-fg-danger)_25%,transparent)]"
            : "border-semantic-fg-warning shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-semantic-fg-warning)_25%,transparent)]"
          : node.planned
            ? "border-dashed border-semantic-border-brand-primary"
            : PILL_BORDER[health]
      } ${receded ? RECEDED : ""}`}
      style={{
        left: p.x - CARD_W / 2,
        top: p.y - CARD_H / 2,
        width: CARD_W,
        height: CARD_H,
        animationDelay: `${p.order * STAGGER_MS}ms`,
      }}
      title={node.detail ? `${node.label} — ${node.detail}` : node.label}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        {logo ? (
          // White under a vendor logo in both themes, like a cloud's plate: the
          // logos are drawn for a light ground.
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-semantic-border-primary bg-white p-1">
            <img
              src={logo}
              alt=""
              aria-hidden
              className="h-full w-full object-contain"
            />
          </span>
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-semantic-bg-brand-secondary text-semantic-text-brand">
            <Icon className="h-4 w-4" aria-hidden />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[health]}`}
              aria-hidden
            />
            <span className="truncate font-heading text-[13px] font-bold text-semantic-text-primary">
              {node.label}
            </span>
          </span>
          {node.detail && (
            <span className="block truncate text-[11px] text-semantic-text-secondary">
              {node.detail}
            </span>
          )}
        </span>
      </span>
      {node.planned && <ProposedTag />}
      {node.planned && <Glow shape="card" delayMs={newAt} color={NEON} />}
      {focus && (
        <Glow
          shape="card"
          delayMs={newAt}
          color={attentionColor(node.status)}
        />
      )}
    </div>
  );
}

function CloudNode({
  p,
  opens,
  newAt,
  receded: isReceded,
}: {
  p: Placed;
  opens: Opens;
  newAt: number;
  receded: boolean;
}) {
  const delay = { animationDelay: `${p.order * STAGGER_MS}ms` };
  const focus = Boolean(p.node.focus) && !p.node.planned;
  const receded = isReceded ? RECEDED : "";
  return (
    <>
      {(p.node.planned || focus) && (
        <span
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
          style={{
            left: p.x,
            top: p.y,
            width: CLOUD_R * 2 + 4,
            height: CLOUD_R * 2 + 4,
          }}
        >
          <Glow
            shape="ring"
            delayMs={newAt}
            color={focus ? attentionColor(p.node.status) : NEON}
          />
        </span>
      )}
      {/* The plate and its caption as one target; the marks below stay inert. */}
      {opens.href && (
        <span
          {...linkProps(opens)}
          aria-label={p.node.label}
          className="absolute z-10 -translate-x-1/2 cursor-pointer rounded-xl hover:bg-semantic-bg-brand-secondary/40"
          style={{
            left: p.x,
            top: p.y - CLOUD_R - 4,
            width: CLOUD_BOX.width - 30,
            height: CLOUD_R * 2 + 40,
          }}
        />
      )}
      <CloudMark
        cloud={cloudOf(p.node)}
        className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 motion-safe:animate-topology-in ${receded}`}
        style={{ left: p.x, top: p.y, ...delay }}
      />
      <span
        className={`pointer-events-none absolute -translate-x-1/2 whitespace-nowrap text-center text-[11px] font-semibold leading-tight motion-safe:animate-topology-in ${
          focus
            ? p.node.status === "down"
              ? "text-semantic-text-danger"
              : "text-semantic-text-warning"
            : "text-semantic-text-brand"
        } ${receded}`}
        style={{ left: p.x, top: p.y + CLOUD_R + 5, ...delay }}
      >
        {p.node.label}
        {p.node.detail && (
          <span className="block font-normal text-semantic-text-secondary">
            {p.node.detail}
          </span>
        )}
      </span>
    </>
  );
}

/** The drawing at its own size; the frame around it scales and pans it. */
function Drawing({
  spec,
  laid,
  withLabels,
  hrefOf,
  onOpen,
  focusOn,
}: {
  spec: DiagramSpec;
  laid: Laid;
  withLabels: boolean;
  hrefOf: (node: DiagramNode) => string | null;
  onOpen: (href: string) => void;
  /** "Show all" off: what the drawing points at stands out, the rest steps back. */
  focusOn: boolean;
}) {
  const byId = new Map(laid.nodes.map((p) => [p.node.id, p]));
  const linesAt = laid.nodes.length * STAGGER_MS;
  // Once every card and line is in, so the flash is the last thing to happen.
  const newAt = linesAt + laid.edges.length * 40 + 450;
  const focusing = focusOn && hasFocus(spec);
  // The two ends of a link in focus are part of what is pointed at: they keep
  // full colour (without a ring of their own — that marks what is affected).
  const nearFocus = new Set(
    spec.edges.filter((e) => e.focus).flatMap((e) => [e.from, e.to]),
  );
  const recedes = (node: DiagramNode) =>
    focusing && !node.focus && !node.planned && !nearFocus.has(node.id);
  const links = laid.edges.flatMap(({ index, points, label }) => {
    const edge = spec.edges[index];
    if (!edge) return [];
    const toCloud = byId.get(edge.to)?.node.kind === "cloud";
    return [{ edge, i: index, d: curve(points), mid: label, toCloud }];
  });

  return (
    <div
      className="relative"
      style={{ width: laid.width, height: laid.height }}
    >
      <svg
        className="absolute inset-0 overflow-visible"
        width={laid.width}
        height={laid.height}
        aria-hidden
      >
        <g fill="none">
          {links.map(({ edge, i, d, toCloud }) => (
            <path
              key={i}
              d={d}
              className={`${
                edge.planned
                  ? "stroke-semantic-border-brand-primary"
                  : edge.focus
                    ? edge.status === "down"
                      ? "stroke-semantic-fg-danger"
                      : "stroke-semantic-fg-warning"
                    : LINE_STROKE[HEALTH[edge.status ?? "unknown"]]
              } motion-safe:animate-topology-in`}
              strokeWidth={2}
              strokeLinecap="round"
              // Dashes as on the Overview: a line into a cloud, and anything proposed.
              strokeDasharray={
                edge.planned ? "3 5" : toCloud ? "6 5" : undefined
              }
              // A link with no reading recedes a little; one with a status reads at full strength.
              strokeOpacity={
                // What a focused drawing does not point at steps back.
                focusing && !edge.focus && !edge.planned
                  ? 0.25
                  : !edge.planned &&
                      !edge.focus &&
                      (edge.status ?? "unknown") === "unknown"
                    ? 0.7
                    : 1
              }
              style={{ animationDelay: `${linesAt + i * 40}ms` }}
            />
          ))}
          {links
            .filter(({ edge }) => edge.planned || edge.focus)
            .map(({ edge, i, d }) => {
              const color = edge.planned ? NEON : attentionColor(edge.status);
              return (
                <path
                  key={`glow-${i}`}
                  d={d}
                  stroke={color}
                  strokeWidth={3}
                  strokeLinecap="round"
                  className="opacity-0 motion-safe:animate-archi-new"
                  style={{
                    filter: `drop-shadow(0 0 4px ${color})`,
                    animationDelay: `${newAt}ms`,
                  }}
                />
              );
            })}
        </g>
        {laid.nodes
          .filter((p) => isCloud(p.node))
          .map((p) => (
            <circle
              key={p.node.id}
              cx={p.x}
              cy={p.y}
              r={CLOUD_R}
              className={`${cloudPlateClass(cloudOf(p.node))} ${
                p.node.planned
                  ? "stroke-semantic-border-brand-primary"
                  : p.node.focus
                    ? p.node.status === "down"
                      ? "stroke-semantic-fg-danger"
                      : "stroke-semantic-fg-warning"
                    : LINE_STROKE[HEALTH[p.node.status ?? "up"]]
              } motion-safe:animate-topology-in ${recedes(p.node) ? RECEDED : ""}`}
              strokeWidth={3}
              strokeDasharray={p.node.planned ? "3 4" : undefined}
              style={{ animationDelay: `${p.order * STAGGER_MS}ms` }}
            />
          ))}
      </svg>

      {withLabels &&
        links.map(({ edge, i, mid }) =>
          mid ? (
            <span
              key={`label-${i}`}
              className="pointer-events-none absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-full border border-semantic-border-brand-secondary bg-semantic-bg-primary py-px pl-1 pr-1.5 text-[10px] font-bold text-semantic-text-brand shadow-sm motion-safe:animate-topology-in"
              style={{
                left: mid[0],
                top: mid[1],
                animationDelay: `${linesAt + i * 40}ms`,
              }}
            >
              <ProviderBadge text={edge.label ?? ""} />
              {edge.label}
            </span>
          ) : null,
        )}

      {laid.nodes.map((p) => {
        const opens = { href: hrefOf(p.node), onOpen };
        return isCloud(p.node) ? (
          <CloudNode
            key={p.node.id}
            p={p}
            opens={opens}
            newAt={newAt}
            receded={recedes(p.node)}
          />
        ) : (
          <NodeCard
            key={p.node.id}
            p={p}
            opens={opens}
            newAt={newAt}
            receded={recedes(p.node)}
          />
        );
      })}
    </div>
  );
}

interface View {
  scale: number;
  x: number;
  y: number;
}

/** The scale and offset that fit the whole drawing in the frame, never above 1:1. */
function fitted(laid: Laid, frame: { width: number; height: number }): View {
  const scale =
    Math.min(1, frame.width / laid.width, frame.height / laid.height) || 1;
  return {
    scale,
    x: (frame.width - laid.width * scale) / 2,
    y: (frame.height - laid.height * scale) / 2,
  };
}

/** What the lines mean, in the Overview legend's place and style. */
const Legend: React.FC<{
  spec: DiagramSpec;
  focusOn: boolean;
  onToggleFocus: () => void;
}> = ({ spec, focusOn, onToggleFocus }) => (
  <div className="absolute bottom-3 left-3 flex items-center gap-3 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-3 py-1.5 text-[11px] text-semantic-text-secondary">
    <span className="flex items-center gap-1.5">
      <span
        className="h-0.5 w-4 rounded bg-semantic-bg-brand-bold"
        aria-hidden
      />{" "}
      Link
    </span>
    <span className="flex items-center gap-1.5">
      <span
        className="w-4 border-t-2 border-dashed border-semantic-bg-brand-bold"
        aria-hidden
      />{" "}
      Cloud
    </span>
    {spec.mode === "scenario" && (
      <span className="flex items-center gap-1.5">
        <span
          className="w-4 border-t-2 border-dotted border-semantic-border-brand-primary"
          aria-hidden
        />{" "}
        Proposed
      </span>
    )}
    {hasFocus(spec) ? (
      <span className="flex items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 rounded-full border-2 border-semantic-fg-warning"
          aria-hidden
        />{" "}
        Needs attention
      </span>
    ) : null}
    {hasFocus(spec) ? (
      <button
        type="button"
        onClick={onToggleFocus}
        aria-pressed={!focusOn}
        className="-my-0.5 rounded px-1.5 py-0.5 font-semibold text-semantic-text-brand hover:bg-semantic-bg-brand-secondary"
      >
        {focusOn ? "Show all" : "Focus"}
      </button>
    ) : (
      <span className="flex items-center gap-1.5">
        <span
          className="h-2 w-2 rounded-full bg-semantic-fg-warning"
          aria-hidden
        />{" "}
        Attention
      </span>
    )}
  </div>
);

const ZoomButton: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-7 min-w-[28px] items-center justify-center px-2 text-[12px] font-semibold text-semantic-text-secondary hover:bg-semantic-bg-secondary hover:text-semantic-text-primary"
  >
    {children}
  </button>
);

/**
 * `compact` is the preview in the chat: fitted, still. Full is the canvas:
 * labels on the links, wheel and buttons to zoom, drag to pan, Fit to return.
 */
/** A press that moves farther than this is a pan, not a click. */
const DRAG_THRESHOLD_PX = 4;

const ArchiDiagram: React.FC<{
  spec: DiagramSpec;
  compact?: boolean;
  /** The page each element opens; none in the chat preview, which opens the canvas. */
  hrefOf?: (node: DiagramNode) => string | null;
  onOpen?: (href: string) => void;
}> = ({ spec, compact = false, hrefOf, onOpen }) => {
  const laid = useMemo(() => layout(spec, !compact), [spec, compact]);
  const frameRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<View | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const [focusOn, setFocusOn] = useState(true);
  /** Set when a press turned into a pan, so the click that ends it opens nothing. */
  const panned = useRef(false);

  useLayoutEffect(() => {
    const element = frameRef.current;
    if (!element) return;
    const measure = () =>
      setFrame({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Fitted until the reader zooms or pans; refitted when the frame resizes before that.
  const fit = useMemo(() => fitted(laid, frame), [laid, frame]);
  const current = view ?? fit;

  const zoomBy = useCallback(
    (factor: number, around?: { x: number; y: number }) => {
      setView((v) => {
        const base = v ?? fit;
        const scale = Math.min(2.5, Math.max(0.25, base.scale * factor));
        const cx = around?.x ?? frame.width / 2;
        const cy = around?.y ?? frame.height / 2;
        const k = scale / base.scale;
        return { scale, x: cx - (cx - base.x) * k, y: cy - (cy - base.y) * k };
      });
    },
    [fit, frame],
  );

  const interactive = !compact;
  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden bg-semantic-bg-secondary ${compact ? "h-52 w-full" : "h-full w-full cursor-grab active:cursor-grabbing"}`}
      onWheel={
        interactive
          ? (e) => {
              const box = e.currentTarget.getBoundingClientRect();
              zoomBy(e.deltaY < 0 ? 1.1 : 1 / 1.1, {
                x: e.clientX - box.left,
                y: e.clientY - box.top,
              });
            }
          : undefined
      }
      onPointerDown={
        interactive
          ? (e) => {
              drag.current = { x: e.clientX, y: e.clientY, moved: false };
              panned.current = false;
            }
          : undefined
      }
      onPointerMove={
        interactive
          ? (e) => {
              if (!drag.current) return;
              const dx = e.clientX - drag.current.x;
              const dy = e.clientY - drag.current.y;
              if (!drag.current.moved) {
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
                // Captured only once it is a pan: capturing on press would take
                // the click away from the element under the pointer.
                e.currentTarget.setPointerCapture(e.pointerId);
                panned.current = true;
              }
              drag.current = { x: e.clientX, y: e.clientY, moved: true };
              setView((v) => {
                const base = v ?? fit;
                return { ...base, x: base.x + dx, y: base.y + dy };
              });
            }
          : undefined
      }
      onPointerUp={interactive ? () => (drag.current = null) : undefined}
      onClickCapture={
        interactive
          ? (e) => {
              if (!panned.current) return;
              panned.current = false;
              e.stopPropagation();
            }
          : undefined
      }
    >
      {frame.width > 0 && (
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            transform: `translate(${current.x}px, ${current.y}px) scale(${current.scale})`,
          }}
        >
          <Drawing
            spec={spec}
            laid={laid}
            withLabels={!compact}
            hrefOf={compact || !hrefOf ? () => null : hrefOf}
            onOpen={onOpen ?? (() => undefined)}
            focusOn={focusOn}
          />
        </div>
      )}
      {interactive && (
        <Legend
          spec={spec}
          focusOn={focusOn}
          onToggleFocus={() => setFocusOn((v) => !v)}
        />
      )}
      {interactive && (
        <div className="absolute bottom-3 right-3 flex items-center divide-x divide-semantic-border-primary overflow-hidden rounded-md border border-semantic-border-primary bg-semantic-bg-primary shadow-sm">
          <ZoomButton label="Zoom out" onClick={() => zoomBy(1 / 1.2)}>
            −
          </ZoomButton>
          <ZoomButton label="Zoom in" onClick={() => zoomBy(1.2)}>
            +
          </ZoomButton>
          <ZoomButton label="Fit the drawing" onClick={() => setView(null)}>
            Fit
          </ZoomButton>
        </div>
      )}
    </div>
  );
};

export default ArchiDiagram;
